// w41 Yaylov (sunny meadow) — sunny-meadow wedding preview card.
//
// The full template opens on a sun-washed meadow: a bright sky over
// rolling green grass, drifting clouds, and falling pink petals. The
// catalog card compresses that into one still frame: a vertical gradient
// running sky-blue -> soft meadow green from top to bottom, a scatter of
// tiny white cloud blobs near the top, a handful of falling pink petal
// dots, a white circle monogram holding the couple's initials, and the
// couple's names in a cream serif treatment with a gold rule + date line
// below. Uses the site's already-loaded serif stack (via `font`) — no new
// font import.
import { type LayoutCtx } from "./_shared";

// Deterministic cloud blobs — same positions/sizes on every render (no
// Math.random so SSR/CSR + repeated mounts stay visually stable). Confined
// to the sky band near the top of the card.
const CLOUDS: Array<{ x: number; y: number; w: number; h: number; o: number }> = [
  { x: 8, y: 10, w: 22, h: 8, o: 0.85 },
  { x: 52, y: 6, w: 28, h: 9, o: 0.75 },
  { x: 68, y: 16, w: 18, h: 7, o: 0.6 },
];

// Deterministic falling petal dots — same positions/sizes on every render.
// Scattered across the full card height so the "falling" motif reads even
// as a still frame.
const PETALS: Array<{ x: number; y: number; r: number; o: number }> = [
  { x: 14, y: 24, r: 3, o: 0.8 },
  { x: 26, y: 38, r: 2.2, o: 0.6 },
  { x: 40, y: 20, r: 2.8, o: 0.7 },
  { x: 58, y: 32, r: 2.4, o: 0.55 },
  { x: 72, y: 22, r: 3, o: 0.75 },
  { x: 86, y: 40, r: 2.2, o: 0.6 },
  { x: 20, y: 55, r: 2.6, o: 0.5 },
  { x: 48, y: 62, r: 2.2, o: 0.45 },
  { x: 80, y: 58, r: 2.8, o: 0.55 },
  { x: 34, y: 78, r: 2.4, o: 0.4 },
  { x: 64, y: 82, r: 2.2, o: 0.4 },
  { x: 90, y: 72, r: 2.6, o: 0.45 },
];

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials joined with a middot, matching the design's splash monogram.
// Falls back to a single initial when only one name is present — no
// dangling separator.
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]}·${letters[1]}` : letters[0] || "";
}

export function YaylovPreviewLayout({ names, date, accent, ink, font }: LayoutCtx) {
  const green = accent || "#6faf4f";
  const cream = "#fdf8ec";
  const petalPink = "#f2b6c6";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Sky blue top -> meadow green base.
        background: `linear-gradient(180deg, #8ec9f2 0%, #cdeab8 45%, ${green} 100%)`,
        padding: "6%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Tiny white cloud blobs, sky band only. */}
      {CLOUDS.map((cloud, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${cloud.x}%`,
            top: `${cloud.y}%`,
            width: `${cloud.w}%`,
            height: `${cloud.h}%`,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: cloud.o,
            filter: "blur(1.5px)",
          }}
        />
      ))}

      {/* Falling pink petal dots. */}
      {PETALS.map((petal, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${petal.x}%`,
            top: `${petal.y}%`,
            width: petal.r * 2,
            height: petal.r * 2,
            borderRadius: "50%",
            background: petalPink,
            opacity: petal.o,
          }}
        />
      ))}

      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${cream}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: cream,
            marginTop: "4%",
            marginBottom: "8%",
            textShadow: "0 1px 4px rgba(51,80,43,0.35)",
          }}
        >
          TO'Y TAKLIFNOMASI
        </div>

        {/* White circle monogram. */}
        <div
          style={{
            width: "30%",
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            marginBottom: "8%",
            display: "grid",
            placeItems: "center",
            background: cream,
            boxShadow: `0 2px 10px rgba(51,80,43,0.25), inset 0 0 0 1px ${green}44`,
          }}
        >
          <span
            style={{
              fontFamily: font,
              fontSize: 13,
              letterSpacing: "0.06em",
              color: ink || "#33502b",
            }}
          >
            {mono}
          </span>
        </div>

        {/* Cream names. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 26,
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            color: cream,
            textShadow: "0 1px 10px rgba(51,80,43,0.3)",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Gold rule. */}
        <div style={{ width: "34%", height: 1, background: "#d9b25ccc", marginBottom: "6%" }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: cream,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
